import { useCallback, useEffect, useState } from 'react'

import { useRouter } from 'next/router'

import playcanvasConfig from '~/config/playcanvas'

import useCurrentUser from '~/hooks/useCurrentUser'

import playCanvasAnalytics from '~/analytics/playcanvas/playcanvasAnalyticsEventLogger'

import { useGetAvatarMetadataQuery } from '~/redux/apis/user-api'

import { useGetPetStateQuery } from '~/shared/apis/pet-state.api'

import { GAME_REWARDS, WINDSURFER_REQUEST } from '~/games/constants/games.const'
import useGameRunTimer from '~/games/hooks/useGameRunTimer'

import { useGetUserItemsQuery } from '~/redux/apis/gem-shop-api'

import { useSetWindSurferStatsMutation, useGetWindSurferStatsQuery } from '~/games/apis/windSurfer-api'
import ROUTES from '~/shared/constants/routes.const'

import { useSetRewardsMutation } from '~/redux/apis/reward-api'
import { REWARD_EVENT, REWARD_FEATURE } from '~/gamification/types/gamification.types'

function useWindSurfer(showBanner: (showTopbar: boolean) => void) {
  const [windSurferIframe, setWindSurferIframe] = useState<HTMLIFrameElement | undefined>(undefined)

  const [isReady, setIsReady] = useState(false)
  const user = useCurrentUser()
  const { data: avatarMetadata } = useGetAvatarMetadataQuery(user, { skip: !user?.avatarUrl })
  const { data: petState } = useGetPetStateQuery(user?.id as number, { skip: !user?.id })

  const router = useRouter()

  const language = router.locale

  const userItems = useGetUserItemsQuery(undefined, { skip: !user?.id })

  const { data: stats } = useGetWindSurferStatsQuery(undefined, { skip: !user?.id })
  const [setUserStats] = useSetWindSurferStatsMutation()

  const [setRewards] = useSetRewardsMutation()

  // The game reports the end of a run but never its start, so the window is inferred here.
  const takeRunWindow = useGameRunTimer(isReady)

  const sendMessageToIframe = useCallback(
    (data: unknown) => {
      const postMessage = windSurferIframe?.contentWindow

      if (postMessage) {
        postMessage.postMessage(data, playcanvasConfig.windSurferURL.origin)
      }
    },
    [windSurferIframe],
  )

  const getNickName = useCallback(() => {
    let nickname = ''

    if (user?.nickname) {
      nickname = user.nickname
    } else if (user?.lastName) {
      nickname = user.lastName
    } else if (user?.firstName) {
      nickname = user.firstName
    }

    return nickname
  }, [user])

  useEffect(
    function initialize() {
      const postMessage = windSurferIframe?.contentWindow
      if (isReady && postMessage && stats) {
        if (user && language && petState) {
          const initialData = {
            type: WINDSURFER_REQUEST.INITIAL_DATA,
            user: {
              id: user.id,
              nickName: getNickName(),
              avatar: avatarMetadata ? avatarMetadata : null,
            },
            petState,
            language,
            userItems: userItems.data,
            userStats: stats,
          }
          sendMessageToIframe(initialData)
        }
      }
    },
    [
      isReady,
      user,
      avatarMetadata,
      language,
      petState,
      sendMessageToIframe,
      windSurferIframe?.contentWindow,
      userItems,
      getNickName,
      stats,
    ],
  )

  const messageHandler = useCallback(
    async (event: any): Promise<void> => {
      if (event.origin === playcanvasConfig.windSurferURL.origin) {
        try {
          const data = event.data
          if (data.type === WINDSURFER_REQUEST.INITIAL_DATA) {
            setIsReady(true)
          }
          if (data.type === WINDSURFER_REQUEST.GET_USERDATA) {
            const msg = {
              type: WINDSURFER_REQUEST.GET_USERDATA,
              userStats: stats,
            }
            sendMessageToIframe(msg)
          }
          if (data.type === WINDSURFER_REQUEST.HIDE_BANNER) {
            showBanner(false)
          }
          if (data.type === WINDSURFER_REQUEST.SHOW_BANNER) {
            showBanner(true)
          }
          if (data.type === WINDSURFER_REQUEST.PLAYCANVAS_ANALYTICS_EVENT) {
            playCanvasAnalytics.logPlayCanvasEvent(data)
          }
          if (data.type === WINDSURFER_REQUEST.MAKE_AVATAR) {
            router.replace(`${ROUTES.AVATAR}?from=windSurfer`)
          }
          if (data.type === GAME_REWARDS.GEMS || data.type === GAME_REWARDS.KUDOS || data.type === GAME_REWARDS.XP) {
            const reward = {
              xp: 0,
              kudos: 0,
              gems: 0,
              feature: REWARD_FEATURE.GAME_WIND_SURFER,
              event_id: `${REWARD_FEATURE.GAME_WIND_SURFER}#${REWARD_EVENT.GAME_ENDED}`,
            }
            switch (data.type) {
              case GAME_REWARDS.GEMS:
                reward.gems = data.amount
                break
              case GAME_REWARDS.KUDOS:
                reward.kudos = data.amount
                break
              case GAME_REWARDS.XP:
                reward.xp = data.amount
                break
            }
            let type = 'no reward'
            let res

            if (reward.gems || reward.kudos || reward.xp) {
              res = await setRewards(reward)
              type = data.type
            }

            const msg = {
              type,
              response: res,
            }

            sendMessageToIframe(msg)
          }

          if (data.type === WINDSURFER_REQUEST.SET_USERDATA) {
            const res = await setUserStats({ ...data.userStats, path: ROUTES.WINDSURFER, ...takeRunWindow() })
            const msg = {
              type: WINDSURFER_REQUEST.SET_USERDATA,
              userStats: res,
            }
            sendMessageToIframe(msg)
          }
        } catch (error) {
          sendMessageToIframe('wrong world')
        }
      }
    },
    [sendMessageToIframe, stats, showBanner, setRewards, setUserStats, router, takeRunWindow],
  )

  useEffect(() => {
    setWindSurferIframe(document.getElementById('windSurferIframe') as HTMLIFrameElement)
  }, [])

  useEffect(() => {
    window.addEventListener('message', messageHandler)
    return () => window.removeEventListener('message', messageHandler)
  }, [messageHandler])

  return {
    windSurferURL: playcanvasConfig.windSurferURL.href,
  }
}

export default useWindSurfer
